import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  Check,
  Code,
  Database,
  FileText,
  LayoutDashboard as Layout,
  Mail,
  Play,
  Radio,
  Share2,
  Smartphone,
  Sparkles,
  Terminal
} from 'lucide-react';
import styled from '../../styled';
import { usePrefersReducedMotion } from '../motion';

const ACCENT = '#2e5cff';
const INK = '#0e1c2b';

type ScrapTone = 'amber' | 'green' | 'red';

type ReportScrap = {
  network: string;
  icon: string;
  x: number; // percent of illustration width
  y: number; // px offset from top of the cloud
  r: number; // scatter rotation in degrees
  size: number; // nominal box height for connector geometry
  fields: ReadonlyArray<string>;
  rawStatus: string;
  tone: ScrapTone;
};

// Six raw network reports, each speaking its own dialect: field names,
// currencies, date formats, and statuses that don't line up.
const SCRAPS: ReportScrap[] = [
  {
    network: 'CJ',
    icon: '/images/icons/cj.svg',
    x: 15,
    y: 22,
    r: -5,
    size: 80,
    fields: ['commission: 18.20 USD', 'posting-date: 07/02/26'],
    rawStatus: 'status: locked',
    tone: 'amber'
  },
  {
    network: 'Skimlinks',
    icon: '/images/icons/skimlinks.jpeg',
    x: 48,
    y: 2,
    r: 3,
    size: 80,
    fields: ['commission_value: €12,40', 'order_ref: SL-88123'],
    rawStatus: 'state: pend.',
    tone: 'amber'
  },
  {
    network: 'Linkby',
    icon: '/images/icons/linkby.svg',
    x: 82,
    y: 26,
    r: 5,
    size: 80,
    fields: ['1,240 clicks × $1.00 cpc', 'campaign: Annual Sale'],
    rawStatus: 'status: live',
    tone: 'green'
  },
  {
    network: 'Webgains',
    icon: '/images/icons/webgains.png',
    x: 22,
    y: 148,
    r: 4,
    size: 80,
    fields: ['commission: £9.10', 'event date: 01/07/2026'],
    rawStatus: 'event: validated',
    tone: 'green'
  },
  {
    network: 'Rakuten',
    icon: '/images/icons/rakuten.png',
    x: 52,
    y: 132,
    r: -3,
    size: 80,
    fields: ['bonus payment: 250.00', 'currency: USD'],
    rawStatus: 'status: payout sent',
    tone: 'green'
  },
  {
    network: 'Awin',
    icon: '/images/icons/awin.png',
    x: 82,
    y: 156,
    r: -6,
    size: 80,
    fields: ['commissionAmount: 6.75', 'clickRef: #92841'],
    rawStatus: 'state: declined',
    tone: 'red'
  }
];

type UnifiedStatus = 'Pending' | 'Confirmed' | 'Rejected' | 'Paid';

const UNIFIED_ROWS: ReadonlyArray<{
  network: string;
  icon: string;
  type: string;
  amount: string;
  status: UnifiedStatus;
}> = [
  { network: 'CJ', icon: '/images/icons/cj.svg', type: 'CPA', amount: '$18.20', status: 'Confirmed' },
  { network: 'Skimlinks', icon: '/images/icons/skimlinks.jpeg', type: 'CPA', amount: '€12.40', status: 'Pending' },
  { network: 'Linkby', icon: '/images/icons/linkby.svg', type: 'CPC', amount: '$1,240.00', status: 'Confirmed' },
  { network: 'Webgains', icon: '/images/icons/webgains.png', type: 'CPA', amount: '£9.10', status: 'Confirmed' },
  { network: 'Rakuten', icon: '/images/icons/rakuten.png', type: 'Bonus', amount: '$250.00', status: 'Paid' },
  { network: 'Awin', icon: '/images/icons/awin.png', type: 'CPA', amount: '$6.75', status: 'Rejected' }
];

const SCENES = [
  {
    step: 'Unify',
    title: 'Affiliate data from 100+ networks, in one place',
    subtitle:
      'Bring transactions, rates, finance data, and payouts into one trusted source of truth.'
  },
  {
    step: 'Optimize',
    title: 'Optimize from traffic source to individual placement',
    subtitle:
      'Tie clicks and revenue to content, traffic sources, and placements. See where revenue is won, lost, and ready to scale.'
  },
  {
    step: 'Amplify',
    title: 'Plan, track, and prove every sponsored campaign',
    subtitle:
      'Sponsored content campaigns as the foundation, link swaps to expand your sellable inventory, and one place to plan, track, and report.'
  }
] as const;

const STEP_COUNT = SCENES.length;
/**
 * Scroll share per scene. Unify gets the most because it plays four
 * beats (scatter → converge → table → fan-out); Amplify's single beat
 * needs the least. Each scene ends with dwell on its settled state so
 * fast scrollers still catch the payoff.
 */
const SCENE_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0, 0.42],
  [0.42, 0.74],
  [0.74, 1]
];

// Geometry of the connector SVG (viewBox units)
const CLOUD_W = 700;
const CLOUD_H = 300;
const FOCUS_X = CLOUD_W / 2;
const FOCUS_Y = CLOUD_H - 4;

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

const useStickyScrollProgress = (
  stageRef: React.RefObject<HTMLDivElement>,
  enabled: boolean
) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const stage = stageRef.current;
    if (!stage || typeof window === 'undefined') {
      return;
    }

    const update = () => {
      const rect = stage.getBoundingClientRect();
      const scrollable = stage.offsetHeight - window.innerHeight;
      if (scrollable <= 0) {
        setProgress(1);
        return;
      }
      setProgress(clamp01(-rect.top / scrollable));
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [stageRef, enabled]);

  return progress;
};

const ScrollStage = styled('div')<{ $reduced: boolean }>`
  height: ${(p) => (p.$reduced ? 'auto' : '400vh')};
  position: relative;

  @media (max-width: 600px) {
    height: ${(p) => (p.$reduced ? 'auto' : '340vh')};
  }
`;

const StickyViewport = styled('div')<{ $reduced: boolean }>`
  position: ${(p) => (p.$reduced ? 'relative' : 'sticky')};
  top: ${(p) => (p.$reduced ? 'auto' : '8vh')};
  height: ${(p) => (p.$reduced ? 'auto' : '84vh')};
  min-height: ${(p) => (p.$reduced ? '0' : '600px')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 600px) {
    min-height: ${(p) => (p.$reduced ? '0' : '520px')};
    height: ${(p) => (p.$reduced ? 'auto' : 'auto')};
    overflow: visible;
  }
`;

const StepCounter = styled('div')`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${ACCENT};
  text-align: center;
  margin-bottom: 12px;
`;

const SceneTitles = styled('div')`
  position: relative;
  height: 86px;
  width: 100%;
  margin-bottom: 18px;

  @media (max-width: 600px) {
    height: 96px;
    margin-bottom: 12px;
  }
`;

const SceneCopy = styled('div')<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  text-align: center;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateY(${(p) => (p.$visible ? 0 : 8)}px);
  transition: opacity 0.45s ease, transform 0.45s ease;
`;

const SceneTitle = styled('h3')`
  margin: 0;
  font-family: 'Merriweather', serif;
  letter-spacing: -2%;
  font-size: 28px;
  font-weight: 700;
  color: ${INK};
  line-height: 1.3;
  padding: 0 24px;

  @media (max-width: 600px) {
    font-size: 19px;
  }
`;

const SceneSubtitle = styled('p')`
  max-width: 650px;
  margin: 8px auto 0;
  padding: 0 24px;
  font-size: 15px;
  line-height: 1.5;
  color: #64748b;

  @media (max-width: 600px) {
    margin-top: 6px;
    font-size: 13px;
    line-height: 1.4;
  }
`;

const SceneStage = styled('div')`
  position: relative;
  width: 100%;
  max-width: 760px;
  height: 520px;
  margin: 0 auto;
  padding: 0 16px;

  @media (max-width: 600px) {
    height: auto;
    min-height: 580px;
    overflow: visible;
  }
`;

/* Soft blue spotlight behind the active illustration */
const StageGlow = styled('div')`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 720px;
  height: 480px;
  transform: translate(-50%, -50%);
  background: radial-gradient(
    ellipse at center,
    rgba(46, 92, 255, 0.09) 0%,
    rgba(46, 92, 255, 0.03) 45%,
    transparent 70%
  );
  pointer-events: none;

  @media (min-width: 961px) {
    width: 830px;
    height: 550px;
  }
`;

const SceneLayer = styled('div')<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  pointer-events: ${(p) => (p.$visible ? 'auto' : 'none')};
  transform: translateY(${(p) => (p.$visible ? 0 : 16)}px);
  transition: opacity 0.55s ease, transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);

  /* Enlarge the mockups on desktop; the illustrations are built on
     fluid containers, so a uniform scale keeps every scene in sync. */
  @media (min-width: 961px) {
    transform: translateY(${(p) => (p.$visible ? 0 : 16)}px) scale(1.15);
  }

  @media (prefers-reduced-motion: reduce) {
    transform: none;

    @media (min-width: 961px) {
      transform: scale(1.15);
    }
  }

  @media (max-width: 600px) {
    justify-content: flex-start;
    padding-top: 4px;
    overflow: visible;
  }
`;

const UnifyStack = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const UnifyHub = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;

  @media (max-width: 600px) {
    margin-top: 10px;
    position: relative;
    z-index: 4;
  }
`;

/* ------------------------- Scene 1: Unify ------------------------- */

const Cloud = styled('div')`
  position: relative;
  height: ${CLOUD_H}px;
  width: 100%;

  @media (max-width: 600px) {
    height: 250px;
    margin-bottom: 4px;
    overflow: visible;
  }
`;

const ConnectorSvg = styled('svg')<{ $drawnIn: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  /* Shrinking a circle centered on the hub retracts every line into it */
  clip-path: circle(${(p) => (p.$drawnIn ? '0%' : '140%')} at 50% 99%);
  transition: clip-path 0.85s cubic-bezier(0.22, 1, 0.36, 1);

  @keyframes connectorFlow {
    from {
      stroke-dashoffset: 0;
    }
    to {
      /* One full dash period (3 + 6) so the loop is seamless */
      stroke-dashoffset: -9;
    }
  }

  path {
    fill: none;
    stroke: rgba(14, 28, 43, 0.18);
    stroke-width: 1;
    stroke-dasharray: 3 6;
    animation: connectorFlow 1.4s linear infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

const ScrapCard = styled('div')<{
  $x: number;
  $y: number;
  $r: number;
  $converged: boolean;
}>`
  position: absolute;
  left: ${(p) => (p.$converged ? 50 : p.$x)}%;
  top: ${(p) => (p.$converged ? CLOUD_H - 30 : p.$y)}px;
  width: 182px;
  margin-left: -91px;
  padding: 9px 12px 10px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 8px 20px rgba(14, 28, 43, 0.1);
  display: flex;
  flex-direction: column;
  gap: 3px;
  transform: rotate(${(p) => (p.$converged ? 0 : p.$r)}deg)
    scale(${(p) => (p.$converged ? 0.15 : 1)});
  opacity: ${(p) => (p.$converged ? 0 : 1)};
  transition: left 0.8s cubic-bezier(0.22, 1, 0.36, 1),
    top 0.8s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.8s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.6s ease 0.15s;

  @media (max-width: 600px) {
    width: 148px;
    margin-left: -74px;
    left: ${(p) => (p.$converged ? 50 : p.$x * 0.9 + 5)}%;
    transform: rotate(${(p) => (p.$converged ? 0 : p.$r)}deg)
      scale(${(p) => (p.$converged ? 0.15 : 0.72)});
    top: ${(p) => (p.$converged ? 196 : p.$y * 0.5)}px;
  }
`;

const ScrapHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;

  img {
    width: 15px;
    height: 15px;
    border-radius: 4px;
    object-fit: contain;
  }

  span {
    font-size: 10.5px;
    font-weight: 700;
    color: ${INK};
  }
`;

const ScrapField = styled('div')`
  font-size: 10px;
  line-height: 1.5;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SCRAP_TONES: Record<ScrapTone, { bg: string; fg: string }> = {
  amber: { bg: '#fef3c7', fg: '#b45309' },
  green: { bg: '#d1fae5', fg: '#047857' },
  red: { bg: '#fee2e2', fg: '#b91c1c' }
};

const ScrapStatus = styled('div')<{ $tone: ScrapTone }>`
  align-self: flex-start;
  margin-top: 3px;
  padding: 1px 7px;
  border-radius: 5px;
  font-size: 9.5px;
  background: ${(p) => SCRAP_TONES[p.$tone].bg};
  color: ${(p) => SCRAP_TONES[p.$tone].fg};
`;

const UNIFIED_STATUS_TONES: Record<UnifiedStatus, { bg: string; fg: string }> = {
  Pending: { bg: '#fef3c7', fg: '#b45309' },
  Confirmed: { bg: '#d1fae5', fg: '#047857' },
  Rejected: { bg: '#fee2e2', fg: '#b91c1c' },
  Paid: { bg: '#dbeafe', fg: '#1d4ed8' }
};

const TableWrap = styled('div')<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 8px;
  pointer-events: none;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateY(${(p) => (p.$visible ? 0 : 14)}px);
  transition: opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s;

  @media (max-width: 600px) {
    z-index: 3;
    padding: 0 2px;
    align-items: center;
  }
`;

const TableCard = styled('div')`
  width: min(540px, 94%);
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(14, 28, 43, 0.12);
  overflow: hidden;

  @media (max-width: 600px) {
    width: 100%;
    max-width: 320px;
  }
`;

const TableRowBase = styled('div')`
  display: grid;
  grid-template-columns: 1.5fr 0.8fr 1fr 1.1fr;
  align-items: center;
  padding: 6px 16px;
`;

const TableHeadRow = styled(TableRowBase)`
  padding-top: 9px;
  padding-bottom: 7px;
  background: #f8fafc;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;

  @media (max-width: 600px) {
    padding: 7px 10px 6px;
    font-size: 8px;
    grid-template-columns: 1.35fr 0.7fr 0.9fr 1fr;
  }
`;

const TableRow = styled(TableRowBase)<{ $visible: boolean; $delay: number }>`
  border-top: 1px solid #f1f5f9;
  font-size: 12px;
  color: #334155;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateY(${(p) => (p.$visible ? 0 : 8)}px);
  transition: opacity 0.45s ease ${(p) => p.$delay}ms,
    transform 0.45s ease ${(p) => p.$delay}ms;

  @media (max-width: 600px) {
    padding: 5px 10px;
    font-size: 10px;
    grid-template-columns: 1.35fr 0.7fr 0.9fr 1fr;
  }
`;

const TableNetworkCell = styled('div')`
  display: flex;
  align-items: center;
  gap: 7px;
  font-weight: 600;
  color: ${INK};

  img {
    width: 15px;
    height: 15px;
    border-radius: 4px;
    object-fit: contain;
  }
`;

const StatusChip = styled('span')<{ $status: UnifiedStatus }>`
  justify-self: start;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 600;
  background: ${(p) => UNIFIED_STATUS_TONES[p.$status].bg};
  color: ${(p) => UNIFIED_STATUS_TONES[p.$status].fg};

  @media (max-width: 600px) {
    padding: 2px 6px;
    font-size: 9px;
  }
`;

const HubBadge = styled('div')<{ $lit: boolean }>`
  position: relative;
  z-index: 2;
  width: 48px;
  height: 48px;
  margin: -52px auto 0;
  border-radius: 13px;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: ${(p) =>
    p.$lit
      ? '0 0 0 6px rgba(46, 92, 255, 0.14), 0 8px 30px rgba(46, 92, 255, 0.35)'
      : '0 6px 18px rgba(14, 28, 43, 0.12)'};
  transition: box-shadow 0.6s ease;

  img {
    /* The asset carries its own padding, so let it fill the badge */
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 600px) {
    width: 42px;
    height: 42px;
    margin: 0 auto;
    border-radius: 11px;
  }
`;

const HubLabel = styled('div')`
  position: relative;
  z-index: 2;
  margin-top: 8px;
  text-align: center;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: ${INK};

  @media (max-width: 600px) {
    margin-top: 6px;
    font-size: 11.5px;
  }
`;

/* Fan-out: mirror of the top funnel, from the badge to the preview cards */
const FAN_W = 640;
const FAN_H = 52;
const FAN_TARGETS = [60, 190, 320, 450, 580];

const FanSvg = styled('svg')<{ $drawnOut: boolean }>`
  display: block;
  width: 100%;
  max-width: ${FAN_W}px;
  height: ${FAN_H}px;
  margin: -6px auto 0;
  overflow: visible;
  /* Growing a circle centered on the badge draws every line outward */
  clip-path: circle(${(p) => (p.$drawnOut ? '150%' : '0%')} at 50% 0%);
  transition: clip-path 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.25s;

  @keyframes fanFlow {
    from {
      stroke-dashoffset: 0;
    }
    to {
      stroke-dashoffset: -9;
    }
  }

  path {
    fill: none;
    stroke: rgba(46, 92, 255, 0.45);
    stroke-width: 1.2;
    stroke-dasharray: 3 6;
    animation: fanFlow 1.4s linear infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

const PreviewGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  width: 100%;
  max-width: ${FAN_W}px;
  margin: 0 auto;

  @media (max-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-top: 10px;
    max-width: 320px;

    & > :nth-child(4) {
      grid-column: 1;
      justify-self: center;
    }

    & > :nth-child(5) {
      grid-column: 3;
      justify-self: center;
    }
  }
`;

const Destination = styled('div')<{ $lit: boolean; $delay: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 84px;
  opacity: ${(p) => (p.$lit ? 1 : 0)};
  transform: translateY(${(p) => (p.$lit ? 0 : 18)}px)
    scale(${(p) => (p.$lit ? 1 : 0.97)});
  transition: opacity 0.5s ease ${(p) => p.$delay}ms,
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${(p) => p.$delay}ms;

  @media (max-width: 600px) {
    gap: 6px;
    min-height: 68px;
  }
`;

const DestinationMark = styled('div')<{ $lit: boolean }>`
  position: relative;
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: linear-gradient(145deg, #fff 20%, #edf7ff 100%);
  box-shadow: ${(p) =>
    p.$lit
      ? '0 0 0 5px rgba(46, 92, 255, 0.08), 0 12px 30px rgba(46, 92, 255, 0.18)'
      : '0 8px 20px rgba(14, 28, 43, 0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${ACCENT};
  transition: box-shadow 0.5s ease;

  @media (max-width: 600px) {
    width: 44px;
    height: 44px;
    border-radius: 14px;
  }
`;

const DestinationLabel = styled('div')`
  font-size: 12.5px;
  font-weight: 700;
  color: ${INK};
  line-height: 1.3;
  text-align: center;
  white-space: nowrap;

  @media (max-width: 600px) {
    font-size: 10.5px;
    white-space: normal;
    max-width: 88px;
  }
`;

const DESTINATIONS = [
  {
    icon: Layout,
    label: 'Web app'
  },
  {
    icon: Sparkles,
    label: 'AI agent'
  },
  {
    icon: Terminal,
    label: 'MCP'
  },
  {
    icon: Code,
    label: 'API'
  },
  {
    icon: Database,
    label: 'Data warehouse'
  }
] as const;

// Tentacles radiate inward from each scrap's hub-facing edge.
const connectorPath = (scrap: ReportScrap) => {
  const cx = (scrap.x / 100) * CLOUD_W;
  const cy = scrap.y + scrap.size / 2;
  const dx = FOCUS_X - cx;
  const dy = FOCUS_Y - cy;
  const dist = Math.hypot(dx, dy) || 1;
  const startOffset = scrap.size / 2 + 18;
  const sx = cx + (dx / dist) * startOffset;
  const sy = cy + (dy / dist) * startOffset;
  const bend = scrap.r * 4;
  const mx = (sx + FOCUS_X) / 2 - (dy / dist) * bend;
  const my = (sy + FOCUS_Y) / 2 + (dx / dist) * bend;
  return `M ${sx} ${sy} Q ${mx} ${my} ${FOCUS_X} ${FOCUS_Y}`;
};

/* ------------------------ Scene 2: Optimize ----------------------- */

const SURFACES = [
  { label: 'Newsletter', icon: Mail },
  { label: 'YouTube', icon: Play },
  { label: 'Social', icon: Share2 },
  { label: 'Syndication', icon: Radio },
  { label: 'Mobile app', icon: Smartphone }
] as const;

const SurfacesFooter = styled('div')`
  width: 100%;
  max-width: 620px;
  margin: 20px auto 0;
  text-align: center;
`;

const SurfacesLabel = styled('div')`
  font-size: 12.5px;
  font-weight: 400;
  color: #64748b;
  margin-bottom: 10px;
`;

const SurfaceChips = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
`;

const SurfaceChip = styled('span')<{ $lit: boolean; $delay: number }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 13px;
  border: 1px solid
    ${(p) => (p.$lit ? 'rgba(46, 92, 255, 0.42)' : '#e2e8f0')};
  border-radius: 100px;
  background: ${(p) => (p.$lit ? '#f0f8ff' : 'rgba(248, 250, 252, 0.9)')};
  color: ${(p) => (p.$lit ? INK : '#64748b')};
  font-size: 11.5px;
  font-weight: 700;
  transform: translateY(${(p) => (p.$lit ? 0 : 3)}px);
  transition: color 0.4s ease ${(p) => p.$delay}ms,
    border-color 0.4s ease ${(p) => p.$delay}ms,
    background 0.4s ease ${(p) => p.$delay}ms,
    transform 0.4s ease ${(p) => p.$delay}ms;

  svg {
    flex-shrink: 0;
    color: ${(p) => (p.$lit ? ACCENT : '#94a3b8')};
    transition: color 0.4s ease ${(p) => p.$delay}ms;
  }
`;

const heatProducts = [
  {
    name: 'Breville Barista Express',
    badge: 'Best overall',
    bubble: { size: 92, color: 'rgba(240, 82, 82, 0.55)', ring: true },
    stats: true
  },
  {
    name: 'Gaggia Classic Pro',
    badge: 'Best for beginners',
    bubble: { size: 64, color: 'rgba(244, 143, 177, 0.5)', ring: false },
    stats: false
  },
  {
    name: 'De’Longhi Dedica',
    badge: 'Budget pick',
    bubble: { size: 42, color: 'rgba(56, 169, 255, 0.5)', ring: false },
    stats: false
  }
];

const ArticleCard = styled('div')`
  position: relative;
  width: 100%;
  max-width: 620px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 22px 24px 60px;
  box-shadow: 0 24px 64px rgba(14, 28, 43, 0.14);
`;

const ArticleTitle = styled('div')`
  font-family: 'Merriweather', serif;
  font-size: 17px;
  font-weight: 700;
  color: #0e1c2b;
  margin-bottom: 6px;
`;

const ArticleByline = styled('div')`
  font-size: 10px;
  color: #94a3b8;
  margin-bottom: 16px;
`;

const ProductRow = styled('div')`
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 0;
  border-top: 1px solid #f1f5f9;
`;

const ProductThumb = styled('div')`
  width: 52px;
  height: 40px;
  border-radius: 6px;
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
  flex-shrink: 0;
`;

const ProductInfo = styled('div')`
  flex: 1;
  min-width: 0;
`;

const ProductName = styled('div')`
  font-size: 12px;
  font-weight: 700;
  color: #0e1c2b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProductBadge = styled('span')`
  display: inline-block;
  font-size: 8px;
  font-weight: 700;
  color: #fff;
  background: #334155;
  border-radius: 3px;
  padding: 2px 6px;
  margin-top: 4px;
`;

const CheckButton = styled('div')`
  position: relative;
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  background: #334155;
  border-radius: 5px;
  padding: 8px 14px;
  white-space: nowrap;
`;

const HeatBubble = styled('div')<{
  $size: number;
  $color: string;
  $selected: boolean;
  $visible: boolean;
}>`
  position: absolute;
  /* Center the bubble over the Check price button (~68px wide, at the row's right edge) */
  right: ${(p) => 34 - p.$size / 2}px;
  top: 50%;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  border: ${(p) =>
    p.$selected ? '2px solid #334155' : '2px solid transparent'};
  transform: translateY(-50%) scale(${(p) => (p.$visible ? 1 : 0)});
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: transform 0.55s cubic-bezier(0.34, 1.56, 0.64, 1),
    opacity 0.4s ease, border-color 0.3s ease;
  pointer-events: none;
  z-index: 1;

  @keyframes bubbleBreathe {
    0%,
    100% {
      transform: translateY(-50%) scale(1);
    }
    50% {
      transform: translateY(-50%) scale(1.08);
    }
  }

  /* Gentle breathing once the pop-in transition has finished. The 0.7s
     delay keeps the animation from overriding the entrance transform. */
  ${(p) =>
    p.$visible &&
    'animation: bubbleBreathe 3.2s ease-in-out 0.7s infinite;'}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const StatsBar = styled('div')<{ $visible: boolean }>`
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #0e1c2b;
  border-radius: 100px;
  padding: 10px 20px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
  transform: translateY(${(p) => (p.$visible ? 0 : 24)}px);
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.4s ease;
  overflow: hidden;
`;

const StatItem = styled('span')<{ $highlight?: 'red' | 'green' }>`
  flex: 1;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
  white-space: nowrap;
  text-align: center;

  strong {
    color: #fff;
    font-weight: 700;
  }

  ${(p) =>
    p.$highlight === 'red' &&
    `
    background: #b03a3a;
    border-radius: 4px;
    padding: 2px 8px;
    color: #fff;

    strong {
      color: #fff;
    }
  `}

  ${(p) =>
    p.$highlight === 'green' &&
    `
    strong {
      background: #b7eb8f;
      color: #135200;
      border-radius: 4px;
      padding: 2px 6px;
    }
  `}

  @media (max-width: 600px) {
    &[data-optional] {
      display: none;
    }
  }
`;

/* ------------------------ Scene 3: Amplify ------------------------ */

const AmplifyFlow = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 12px;
  align-items: start;
  width: 100%;
  max-width: 620px;
  margin: 0 auto;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    max-width: 280px;
  }
`;

const FlowArrow = styled('div')<{ $lit: boolean }>`
  display: flex;
  align-items: center;
  align-self: center;
  color: ${(p) => (p.$lit ? ACCENT : '#cbd5e1')};
  transition: color 0.5s ease;

  @media (max-width: 600px) {
    justify-content: center;
    transform: rotate(90deg);
  }
`;

const AmplifyCard = styled('div')<{ $active: boolean; $complete?: boolean }>`
  padding: 16px 14px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  box-sizing: border-box;
  border: 1px solid
    ${(p) => (p.$active ? 'rgba(46, 92, 255, 0.48)' : '#e2e8f0')};
  border-radius: 14px;
  background: #fff;
  box-shadow: ${(p) =>
    p.$active
      ? '0 16px 40px rgba(46, 92, 255, 0.15)'
      : '0 8px 24px rgba(14, 28, 43, 0.05)'};
  opacity: ${(p) => (p.$active || p.$complete ? 1 : 0.55)};
  transform: translateY(${(p) => (p.$active ? 0 : 5)}px);
  transition: border-color 0.5s ease, box-shadow 0.5s ease,
    opacity 0.5s ease, transform 0.5s ease;
`;

const FlowKicker = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;

  svg {
    color: ${ACCENT};
  }
`;

const FlowValue = styled('div')`
  margin-top: 10px;
  font-size: 20px;
  font-weight: 800;
  color: ${INK};
  line-height: 1.2;
`;

const FlowCaption = styled('div')`
  margin-top: 7px;
  font-size: 11px;
  color: #64748b;
  line-height: 1.45;
`;

const FlowMeta = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;

  span {
    padding: 4px 7px;
    border-radius: 100px;
    background: #f1f5f9;
    font-size: 9px;
    font-weight: 600;
    color: #475569;
  }
`;

const SpendBreakdown = styled('div')`
  margin-top: 10px;
`;

const SpendBar = styled('div')`
  display: flex;
  height: 8px;
  border-radius: 100px;
  overflow: hidden;
  background: #f1f5f9;
`;

const SpendSegment = styled('div')<{ $width: number; $color: string }>`
  width: ${(p) => p.$width}%;
  background: ${(p) => p.$color};
`;

const SpendLegend = styled('div')`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const SpendLegendRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 9px;
  color: #64748b;
`;

const SpendLegendLabel = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const SpendDot = styled('span')<{ $color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 2px;
  background: ${(p) => p.$color};
  flex-shrink: 0;
`;

const SpendAmount = styled('span')`
  font-weight: 700;
  color: ${INK};
  white-space: nowrap;
`;

const TrackingChart = styled('div')`
  margin-top: 8px;
`;

const TrackingChartHead = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 9px;
  font-weight: 600;
  color: #94a3b8;

  .goal {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: 500;
  }

  .dash {
    display: inline-block;
    width: 12px;
    border-top: 2px dashed #cbd5e1;
  }
`;

const PacingRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  font-size: 11px;
  font-weight: 700;
  color: ${INK};
`;

const PacingNote = styled('span')`
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
`;

const PACING_BARS = [14, 22, 30, 38, 46, 54, 62];

const SPEND_PLACEMENTS = [
  { label: '1× Dedicated content', amount: '$14K', width: 56, color: '#2e5cff' },
  { label: '2× Email blast', amount: '$11K', width: 44, color: '#7fa3ff' }
] as const;

const TRACKING_DIMENSIONS = [
  'Content',
  'Products',
  'Links',
  'Placements'
] as const;

/* ------------------------------ Panel ----------------------------- */

export const CommerceLoopPanel = () => {
  const reduced = usePrefersReducedMotion();
  const stageRef = useRef<HTMLDivElement>(null);
  const progress = useStickyScrollProgress(stageRef, !reduced);

  const step = reduced
    ? STEP_COUNT - 1
    : progress < SCENE_RANGES[0][1]
      ? 0
      : progress < SCENE_RANGES[1][1]
        ? 1
        : 2;

  const local = (index: number) => {
    if (reduced) {
      return 1;
    }
    const range = SCENE_RANGES[index];
    return range ? clamp01((progress - range[0]) / (range[1] - range[0])) : 0;
  };

  /*
   * Beat thresholds within each scene's local progress. Beats front-load
   * into the first ~60% so every scene holds its settled state for the
   * final stretch — fast scrollers still see each payoff before the
   * next scene takes over.
   */
  const converged = local(0) > 0.3;
  const tableVisible = reduced || local(0) > 0.42;
  const fannedOut = reduced || local(0) > 0.58;
  const optimizeProgress = local(1);
  const optimizeFocused = step === 1;
  const bubblesVisible = optimizeFocused && optimizeProgress > 0.08;
  const placementSelected = optimizeFocused && optimizeProgress > 0.2;
  const statsVisible = optimizeFocused && optimizeProgress > 0.34;
  const surfacesLit = reduced || (optimizeFocused && optimizeProgress > 0.5);
  const inFlight = reduced || (step === 2 && local(2) > 0.38);

  const scene = SCENES[step];

  return (
    <ScrollStage ref={stageRef} $reduced={reduced}>
      <StickyViewport $reduced={reduced}>
        <StepCounter aria-hidden>
          {`0${step + 1} · ${scene.step}`}
        </StepCounter>

        <SceneTitles>
          {SCENES.map((s, index) => (
            <SceneCopy key={s.step} $visible={index === step}>
              <SceneTitle>{s.title}</SceneTitle>
              <SceneSubtitle>{s.subtitle}</SceneSubtitle>
            </SceneCopy>
          ))}
        </SceneTitles>

        <SceneStage>
          <StageGlow aria-hidden />
          {/* Scene 1: Unify */}
          <SceneLayer $visible={step === 0}>
            <UnifyStack>
            <Cloud>
              {!reduced && (
                <>
                  <ConnectorSvg
                    viewBox={`0 0 ${CLOUD_W} ${CLOUD_H}`}
                    preserveAspectRatio="none"
                    aria-hidden
                    $drawnIn={converged}
                  >
                    {SCRAPS.map((scrap) => (
                      <path key={scrap.network} d={connectorPath(scrap)} />
                    ))}
                  </ConnectorSvg>
                  {SCRAPS.map((scrap) => (
                    <ScrapCard
                      key={scrap.network}
                      $x={scrap.x}
                      $y={scrap.y}
                      $r={scrap.r}
                      $converged={converged}
                    >
                      <ScrapHeader>
                        <img src={scrap.icon} alt="" />
                        <span>{scrap.network}</span>
                      </ScrapHeader>
                      {scrap.fields.map((field) => (
                        <ScrapField key={field}>{field}</ScrapField>
                      ))}
                      <ScrapStatus $tone={scrap.tone}>{scrap.rawStatus}</ScrapStatus>
                    </ScrapCard>
                  ))}
                </>
              )}
              <TableWrap $visible={tableVisible}>
                <TableCard>
                  <TableHeadRow>
                    <div>Network</div>
                    <div>Type</div>
                    <div>Amount</div>
                    <div>Status</div>
                  </TableHeadRow>
                  {UNIFIED_ROWS.map((row, index) => (
                    <TableRow
                      key={row.network}
                      $visible={tableVisible}
                      $delay={index * 90}
                    >
                      <TableNetworkCell>
                        <img src={row.icon} alt="" />
                        {row.network}
                      </TableNetworkCell>
                      <div>{row.type}</div>
                      <div>{row.amount}</div>
                      <StatusChip $status={row.status}>{row.status}</StatusChip>
                    </TableRow>
                  ))}
                </TableCard>
              </TableWrap>
            </Cloud>

            <UnifyHub>
            <HubBadge $lit={converged}>
              <img src="/images/icon-white-padded.png" alt="Affilimate" />
            </HubBadge>
            <HubLabel>Affilimate</HubLabel>
            </UnifyHub>

            <FanSvg
              viewBox={`0 0 ${FAN_W} ${FAN_H}`}
              preserveAspectRatio="none"
              aria-hidden
              $drawnOut={fannedOut}
            >
              {FAN_TARGETS.map((x) => (
                <path
                  key={x}
                  d={`M ${FAN_W / 2} 0 C ${FAN_W / 2} 28, ${x} 24, ${x} ${FAN_H}`}
                />
              ))}
            </FanSvg>

            <PreviewGrid>
              {DESTINATIONS.map((dest, index) => {
                const Icon = dest.icon;
                return (
                  <Destination
                    key={dest.label}
                    $lit={fannedOut}
                    $delay={index * 110}
                  >
                    <DestinationMark $lit={fannedOut}>
                      <Icon size={20} strokeWidth={2} />
                    </DestinationMark>
                    <DestinationLabel>{dest.label}</DestinationLabel>
                  </Destination>
                );
              })}
            </PreviewGrid>
            </UnifyStack>
          </SceneLayer>

          {/* Scene 2: Optimize */}
          <SceneLayer $visible={step === 1}>
            <ArticleCard>
              <ArticleTitle>The 8 Best Espresso Machines of 2026</ArticleTitle>
              <ArticleByline>
                Updated March 2026 · 14 min read
              </ArticleByline>
              {heatProducts.map((product) => (
                <ProductRow key={product.name}>
                  <ProductThumb />
                  <ProductInfo>
                    <ProductName>{product.name}</ProductName>
                    <ProductBadge>{product.badge}</ProductBadge>
                  </ProductInfo>
                  <CheckButton>Check price</CheckButton>
                  <HeatBubble
                    $size={product.bubble.size}
                    $color={product.bubble.color}
                    $selected={product.bubble.ring && placementSelected}
                    $visible={bubblesVisible || reduced}
                  />
                </ProductRow>
              ))}
              <StatsBar $visible={statsVisible || reduced}>
                <StatItem>
                  <strong>1.1K</strong> Clicks
                </StatItem>
                <StatItem $highlight="green">
                  <strong>18.6%</strong> CTR
                </StatItem>
                <StatItem $highlight="red">
                  <strong>$3.2K</strong> from 52 sales
                </StatItem>
                <StatItem>
                  <strong>$32K</strong> GMV
                </StatItem>
                <StatItem>
                  <strong>$2.91</strong> EPC
                </StatItem>
                <StatItem data-optional>
                  <strong>59%</strong> Seen
                </StatItem>
              </StatsBar>
            </ArticleCard>
            <SurfacesFooter>
              <SurfacesLabel>
                Across every surface where you&apos;re present
              </SurfacesLabel>
              <SurfaceChips>
                {SURFACES.map((surface, index) => {
                  const Icon = surface.icon;
                  return (
                    <SurfaceChip
                      key={surface.label}
                      $lit={surfacesLit}
                      $delay={index * 90}
                    >
                      <Icon size={13} strokeWidth={2} />
                      {surface.label}
                    </SurfaceChip>
                  );
                })}
                <SurfaceChip $lit={surfacesLit} $delay={SURFACES.length * 90}>
                  + more
                </SurfaceChip>
              </SurfaceChips>
            </SurfacesFooter>
          </SceneLayer>

          {/* Scene 3: Amplify */}
          <SceneLayer $visible={step === 2}>
            <AmplifyFlow>
              <AmplifyCard $active={!inFlight} $complete={inFlight}>
                <FlowKicker>
                  <FileText size={12} /> Sponsored campaign
                </FlowKicker>
                <FlowValue>Fujifilm Promo Q4</FlowValue>
                <FlowCaption>
                  $25K flat spend · Oct 1 to Dec 31 · tracked campaign links
                </FlowCaption>
                <SpendBreakdown>
                  <SpendBar>
                    {SPEND_PLACEMENTS.map((placement) => (
                      <SpendSegment
                        key={placement.label}
                        $width={placement.width}
                        $color={placement.color}
                      />
                    ))}
                  </SpendBar>
                  <SpendLegend>
                    {SPEND_PLACEMENTS.map((placement) => (
                      <SpendLegendRow key={placement.label}>
                        <SpendLegendLabel>
                          <SpendDot $color={placement.color} />
                          {placement.label}
                        </SpendLegendLabel>
                        <SpendAmount>{placement.amount}</SpendAmount>
                      </SpendLegendRow>
                    ))}
                  </SpendLegend>
                </SpendBreakdown>
                <FlowMeta>
                  <span>3 placements</span>
                </FlowMeta>
              </AmplifyCard>
              <FlowArrow $lit={inFlight} aria-hidden>
                <ArrowRight size={18} />
              </FlowArrow>
              <AmplifyCard $active={inFlight}>
                <FlowKicker>
                  <Check size={12} /> In-flight tracking
                </FlowKicker>
                <FlowValue>93% of goal</FlowValue>
                <TrackingChart aria-hidden>
                  <TrackingChartHead>
                    <span>Target GMV</span>
                    <span className="goal">
                      <span className="dash" />
                      Goal
                    </span>
                  </TrackingChartHead>
                  <svg
                    viewBox="0 0 100 36"
                    preserveAspectRatio="none"
                    style={{ width: '100%', height: 44, display: 'block' }}
                  >
                    <line
                      x1="0"
                      y1="6"
                      x2="100"
                      y2="6"
                      stroke="#cbd5e1"
                      strokeWidth="1"
                      strokeDasharray="3 2"
                    />
                    {PACING_BARS.map((height, index) => (
                      <rect
                        key={height}
                        x={5 + index * 13}
                        y={36 - height * 0.45}
                        width="8"
                        height={height * 0.45}
                        fill={ACCENT}
                        rx="1.5"
                        opacity={0.35 + index * 0.09}
                      />
                    ))}
                  </svg>
                  <PacingRow>
                    <span>On pace</span>
                    <PacingNote>21 days left</PacingNote>
                  </PacingRow>
                </TrackingChart>
                <FlowMeta>
                  {TRACKING_DIMENSIONS.map((dimension) => (
                    <span key={dimension}>{dimension}</span>
                  ))}
                </FlowMeta>
              </AmplifyCard>
            </AmplifyFlow>
          </SceneLayer>
        </SceneStage>
      </StickyViewport>
    </ScrollStage>
  );
};
